import { Component, Input, OnChanges, SimpleChanges, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { NgApexchartsModule } from "ng-apexcharts";
import { CoreService } from "../../../services/core.service";
import { ChartComponent } from "ng-apexcharts";

@Component({
  selector: "app-social-ai-engagement-chart",
  standalone: true,
  imports: [CommonModule, NgApexchartsModule],
  templateUrl: "./social-ai-engagement-chart.component.html",
  styleUrls: ["./social-ai-engagement-chart.component.scss"],
})
export class SocialAiEngagementChartComponent implements OnChanges {
  @Input() selectedSiteId: number | null = null;
  @Input() selectedPlatformId: number | null = null;
  @Input() selectedPeriod: string | null = null;
  @Input() pages: any[] = [];
  @Input() title: string = "";
  @ViewChild("chartRef", { static: false })
  chart!: ChartComponent;

  metricValue = 0;
  metricDiff = 0;
  chartOptions: any = {};
  companyID: number | null = null;
  isLoaded = false;

  private lastPayloadKey: string | null = null;

  constructor(private coreService: CoreService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !this.selectedSiteId ||
      !this.selectedPlatformId ||
      !this.selectedPeriod ||
      !this.pages?.length
    ) return;

    const firstPage = this.pages[0];
    const payloadKey = JSON.stringify({
      site: this.selectedSiteId,
      platform: this.selectedPlatformId,
      period: this.selectedPeriod,
      smid: firstPage.social_media_id,
      token: firstPage.access_token,
    });

    if (payloadKey === this.lastPayloadKey) return;
    this.lastPayloadKey = payloadKey;

    this.loadEngagementData();
  }

  loadEngagementData(): void {
    this.companyID = this.coreService.user?.companyID || 0;
    const payload = {
      content_site_id: this.selectedSiteId,
      social_platform_id: this.selectedPlatformId,
      period: this.selectedPeriod,
      pages: this.pages,
    };

    this.isLoaded = false;

    this.coreService
      .postData(
        `querymetrics/parseFbAnalytics/${this.companyID}?metric=page_post_engagements`,
        payload
      )
      .subscribe({
        next: (res) => {
          const data = res.data?.[0]?.insight?.data?.[0]?.values || [];
          const values = data.map((v: any) => ({
            date: new Date(v.end_time),
            value: v.value,
          }));

          this.metricValue = values.at(-1)?.value || 0;
          this.metricDiff = this.metricValue - (values[0]?.value || 0);

          const categories = values.map((v: any) =>
            v.date.toLocaleDateString("el-GR", { day: "2-digit", month: "2-digit", year: "numeric" })
          );

          this.chartOptions = {
            series: [{ name: "Engagement", data: values.map((v: any) => v.value) }],
            chart: {
              type: "area",
              height: 360,
              toolbar: { show: false },
              zoom: { enabled: false },
            },
            stroke: { curve: "smooth", width: 2, colors: ["#6ab7ff"] },
            dataLabels: { enabled: false },
            grid: {
              borderColor: "#f1f3f4",
              strokeDashArray: 0,
              yaxis: { lines: { show: true } },
              xaxis: { lines: { show: false } },
            },
            yaxis: {
              tickAmount: 6,
              labels: {
                style: { colors: "#777", fontSize: "12px" },
                formatter: (val: number, index: number) =>
                  index === 0 ? "" : val.toLocaleString(),
              },
            },
            xaxis: {
              categories,
              tickAmount: 6,
              labels: { style: { colors: "#777", fontSize: "12px" } },
            },
            tooltip: {
              theme: "light",
              y: { formatter: (val: number) => val.toLocaleString("el-GR") },
            },
            legend: {
              show: true,
              position: "bottom",
              horizontalAlign: "center",
              labels: { colors: "#888" },
            },
            title: {
              align: "left",
              style: {
                fontSize: "15px",
                fontWeight: "600",
                color: "#333",
              },
            },
          };
        },
        error: () => {
          this.metricValue = 0;
        },
        complete: () => {
          this.isLoaded = true;
        },
      });
  }

    async exportChartImage(): Promise<string> {

    if (!this.chart) {
      throw new Error('Reach chart not ready');
    }

    const data = await this.chart.dataURI();
    return data.imgURI;
  }
}
